import type {
  DayType,
  LocalDate,
  MealItem,
  MealSlot,
  TrainingTimeOfDay,
} from '@cadencia/contracts';
import { requireFood } from '../catalog/foods.js';
import { RECIPES, requireRecipe, type Recipe } from '../catalog/recipes.js';
import { daysBetweenLocal } from '../units/local-date.js';
import { roundTo } from '../units/round.js';
import {
  itemsFiberG,
  itemsMacros,
  recipeScaleBounds,
  recipeToItems,
  recipeToItemsScaled,
  roundMacros,
  sumMacros,
  ZERO_MACROS,
} from './composition.js';
import { splitDayIntoSlots } from './meal-split.js';
import { normalizeMealSlots } from './meal-schedule.js';
import type { MacroTargets } from './targets.js';

/**
 * Generador de menu semanal (docs/NUTRITION_ENGINE.md §5).
 *
 * DETERMINISTA y sin reloj ni aleatoriedad (ADR-007): las mismas entradas
 * producen el mismo menu. Claude podra ampliar el catalogo de recetas fuera de
 * linea, pero elegir y escalar cada dia se hace aqui, donde se puede probar.
 */

/** Tolerancias de cierre del dia (§5.3). */
export const MENU_TOLERANCES = {
  kcal: 0.03,
  proteinBelow: 0.03,
  proteinAbove: 0.1,
  carbs: 0.08,
  fat: 0.08,
} as const;

/** Pesos de la seleccion ponderada (§5.2 paso 3). */
export const MENU_WEIGHTS = {
  macroFit: 0.35,
  variety: 0.25,
  favorite: 0.2,
  pantry: 0.1,
  quality: 0.1,
} as const;

export const MENU_RULES = {
  defaultVarietyWindowDays: 10,
  maxUsesPerWeek: 2,
  minFishDays: 2,
  minLegumeDays: 2,
  maxConsecutiveSameProtein: 3,
  localSearchIterations: 200,
  /** Paso del barrido de escalado. Mas fino no cambia el resultado util. */
  scaleStep: 0.02,
  scaleRounds: 3,
  /** Paso y pasadas del afinado por ingrediente (§5.2, paso 4). */
  ingredientStep: 0.05,
  ingredientRounds: 24,
  maxAdjustmentItems: 3,
} as const;

/**
 * Items de ajuste del §5.3: lo que se anade cuando el dia no cierra por poco.
 * Son alimentos sueltos y comunes, no recetas: el objetivo es cuadrar el dia
 * sin inventar un plato que nadie ha pedido.
 */
export const ADJUSTMENT_ITEMS: readonly { foodSlug: string; maxAmount: number; step: number }[] = [
  { foodSlug: 'yogur-proteico', maxAmount: 300, step: 25 },
  { foodSlug: 'queso-batido-0', maxAmount: 250, step: 25 },
  // Los dos amiláceos suben de 80 a 160 g con el esquema configurable
  // (08/09/2026). CUANTAS MENOS COMIDAS, MAS GRANDE ES EL RESIDUO: con cinco
  // platos el dia cierra practicamente solo, y con tres los hidratos se
  // quedaban entre un 3 y un 8 % cortos —del orden de 30 a 40 g— porque las
  // recetas ya estaban en su tope de escalado. Ochenta gramos de arroz no
  // llegaban a taparlo y el dia entero salia `partial` por un pun~ado de arroz.
  // No es una holgura nueva: el item de ajuste sigue an~adiendose solo cuando
  // reduce el error, y siguen siendo tres como maximo.
  { foodSlug: 'arroz-blanco', maxAmount: 160, step: 10 },
  { foodSlug: 'pan-integral', maxAmount: 160, step: 10 },
  { foodSlug: 'manzana', maxAmount: 250, step: 25 },
  { foodSlug: 'platano', maxAmount: 250, step: 25 },
  { foodSlug: 'aceite-oliva-virgen-extra', maxAmount: 20, step: 5 },
  { foodSlug: 'almendra', maxAmount: 40, step: 5 },
];

// --------------------------------------------------------------------------
// Error y tolerancias
// --------------------------------------------------------------------------

export interface MacroDeviation {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
}

/** Desviacion relativa dividida por su tolerancia: <= 1 significa «cierra». */
export function normalizedDeviation(actual: MacroTargets, target: MacroTargets): MacroDeviation {
  const rel = (valor: number, objetivo: number, tolerancia: number): number => {
    if (objetivo <= 0) return valor <= 0 ? 0 : 10;
    return Math.abs(valor - objetivo) / objetivo / tolerancia;
  };
  const proteinaRel =
    target.proteinG <= 0
      ? 0
      : Math.abs(actual.proteinG - target.proteinG) /
        target.proteinG /
        (actual.proteinG < target.proteinG
          ? MENU_TOLERANCES.proteinBelow
          : MENU_TOLERANCES.proteinAbove);

  return {
    kcal: rel(actual.kcal, target.kcal, MENU_TOLERANCES.kcal),
    protein: proteinaRel,
    carbs: rel(actual.carbsG, target.carbsG, MENU_TOLERANCES.carbs),
    fat: rel(actual.fatG, target.fatG, MENU_TOLERANCES.fat),
  };
}

export function closesWithinTolerance(actual: MacroTargets, target: MacroTargets): boolean {
  const desvio = normalizedDeviation(actual, target);
  return desvio.kcal <= 1 && desvio.protein <= 1 && desvio.carbs <= 1 && desvio.fat <= 1;
}

function errorScore(actual: MacroTargets, target: MacroTargets): number {
  const desvio = normalizedDeviation(actual, target);
  return desvio.kcal ** 2 + desvio.protein ** 2 + desvio.carbs ** 2 + desvio.fat ** 2;
}

// --------------------------------------------------------------------------
// Curva de escalado
// --------------------------------------------------------------------------

interface MealCurve {
  recipe: Recipe;
  fixed: MacroTargets;
  scalable: MacroTargets;
  min: number;
  max: number;
}

const CURVAS = new Map<string, MealCurve>();

function mealCurve(recipe: Recipe): MealCurve {
  const guardada = CURVAS.get(recipe.slug);
  if (guardada) return guardada;

  const macrosDe = (filtro: (escalable: boolean) => boolean): MacroTargets =>
    itemsMacros(
      recipe.ingredients
        .filter((ingrediente) => filtro(ingrediente.isScalable))
        .map((ingrediente) => ({
          foodSlug: ingrediente.foodSlug,
          amount: ingrediente.amount,
          unit: ingrediente.unit,
        })),
    );

  const bounds = recipeScaleBounds(recipe);
  const curva: MealCurve = {
    recipe,
    fixed: macrosDe((escalable) => !escalable),
    scalable: macrosDe((escalable) => escalable),
    min: bounds.min,
    max: bounds.max,
  };
  CURVAS.set(recipe.slug, curva);
  return curva;
}

function macrosAt(curva: MealCurve, factor: number): MacroTargets {
  return {
    kcal: curva.fixed.kcal + curva.scalable.kcal * factor,
    proteinG: curva.fixed.proteinG + curva.scalable.proteinG * factor,
    carbsG: curva.fixed.carbsG + curva.scalable.carbsG * factor,
    fatG: curva.fixed.fatG + curva.scalable.fatG * factor,
  };
}

function pasosDeEscala(curva: MealCurve): number[] {
  const pasos: number[] = [];
  for (let f = curva.min; f <= curva.max + 1e-9; f += MENU_RULES.scaleStep) {
    pasos.push(roundTo(f, 3));
  }
  if (pasos.at(-1) !== curva.max) pasos.push(curva.max);
  return pasos;
}

// --------------------------------------------------------------------------
// Calidad nutricional del plato (§5.2, factor de calidad)
// --------------------------------------------------------------------------

const CALIDAD = new Map<string, number>();

function qualityScore(recipe: Recipe): number {
  const guardada = CALIDAD.get(recipe.slug);
  if (guardada !== undefined) return guardada;

  const items = recipeToItems(recipe);
  const fibra = itemsFiberG(items);
  const verdura = items
    .filter((item) => requireFood(item.foodSlug).section === 'produce')
    .reduce((total, item) => total + item.amount, 0);
  const grasaBuena = items.some((item) =>
    ['aceite-oliva-virgen-extra', 'aguacate', 'nuez', 'almendra', 'semillas-chia'].includes(
      item.foodSlug,
    ),
  );
  const capricho = items.some((item) => requireFood(item.foodSlug).isTreat);

  const valor =
    0.4 * Math.min(1, fibra / 12) +
    0.4 * Math.min(1, verdura / 300) +
    (grasaBuena ? 0.2 : 0) -
    (capricho ? 0.2 : 0);
  const acotado = Math.min(1, Math.max(0, valor));
  CALIDAD.set(recipe.slug, acotado);
  return acotado;
}

// --------------------------------------------------------------------------
// Entradas
// --------------------------------------------------------------------------

export interface MenuHistoryEntry {
  recipeSlug: string;
  usedOn: LocalDate;
}

export interface MenuContext {
  bodyWeightKg: number;
  varietyWindowDays?: number;
  favorites?: readonly string[];
  history?: readonly MenuHistoryEntry[];
  pantryFoodSlugs?: readonly string[];
  /** Alimentos excluidos por alergia, intolerancia o preferencia. */
  excludedFoodSlugs?: readonly string[];
}

export interface DayRequest {
  date: LocalDate;
  dayType: DayType;
  target: MacroTargets;
  /**
   * Las comidas de ese dia. Vienen del usuario
   * (`user_preferences.meal_slots`), no de la energia del dia.
   *
   * OBLIGATORIO, y no opcional con un valor por defecto: hasta el 08/09/2026
   * los huecos los elegia `slotsForDay` a partir de las kcal, asi que el
   * esquema de comidas era una consecuencia del objetivo calorico y no una
   * decision de nadie. Un parametro opcional aqui dejaria un llamador
   * generando menus con comidas que el usuario no hace, sin enterarse.
   */
  slots: readonly MealSlot[];
  /**
   * Turno de la sesion de fuerza de ese dia, ya resuelto
   * (`resolveTrainingTimeOfDay`). `null` o ausente = no hay sesion de fuerza,
   * y entonces no hay reparto peri-entrenamiento (§17 del encargo).
   */
  trainingTimeOfDay?: TrainingTimeOfDay | null;
}

export interface MealDraft {
  slot: MealSlot;
  recipeSlug: string;
  items: MealItem[];
  macros: MacroTargets;
  scale: number;
  adjustmentItems: MealItem[];
}

export interface DayMenu {
  date: LocalDate;
  dayType: DayType;
  target: MacroTargets;
  meals: MealDraft[];
  totals: MacroTargets;
  status: 'ok' | 'partial';
  deviation: MacroDeviation;
  reasonCodes: string[];
}

export interface WeeklyMenu {
  days: DayMenu[];
  reasonCodes: string[];
  /** Dias con pescado y con legumbre, para verificar el §5.4 desde fuera. */
  fishDays: number;
  legumeDays: number;
}

// --------------------------------------------------------------------------
// Seleccion
// --------------------------------------------------------------------------

interface SeleccionEstado {
  usosSemana: Map<string, number>;
  ultimoUso: Map<string, LocalDate>;
  /**
   * El historial que llego de fuera, intacto. Hace falta para poder DESHACER
   * una eleccion: cuando la busqueda local descarta una receta que acababa de
   * elegir, su `ultimoUso` tiene que volver a lo que decia el historial y no
   * quedarse apuntando a hoy. Si no, cada receta probada y descartada quedaba
   * bloqueada diez dias y el catalogo disponible se estrechaba solo.
   */
  historial: ReadonlyMap<string, LocalDate>;
  /** Fuente proteica principal de cada dia ya planificado, en orden. */
  fuentesPorDia: string[];
}

/** Deshace el apunte de uso de una receta que finalmente no se usa. */
function liberarUso(estado: SeleccionEstado, slug: string): void {
  const usos = (estado.usosSemana.get(slug) ?? 1) - 1;
  if (usos > 0) {
    estado.usosSemana.set(slug, usos);
    return;
  }
  estado.usosSemana.delete(slug);
  const previo = estado.historial.get(slug);
  if (previo === undefined) estado.ultimoUso.delete(slug);
  else estado.ultimoUso.set(slug, previo);
}

function esApta(
  recipe: Recipe,
  slot: MealSlot,
  fecha: LocalDate,
  estado: SeleccionEstado,
  ctx: MenuContext,
  fuentesDelDia: ReadonlySet<string>,
  ventana: number,
): boolean {
  if (!recipe.slots.includes(slot)) return false;

  const excluidos = new Set(ctx.excludedFoodSlugs ?? []);
  if (recipe.ingredients.some((ingrediente) => excluidos.has(ingrediente.foodSlug))) return false;

  if ((estado.usosSemana.get(recipe.slug) ?? 0) >= MENU_RULES.maxUsesPerWeek) return false;

  const ultimo = estado.ultimoUso.get(recipe.slug);
  if (ultimo !== undefined && daysBetweenLocal(ultimo, fecha) < ventana) return false;

  // No se repite la fuente proteica principal dentro del mismo dia.
  if (recipe.proteinSource !== 'none' && fuentesDelDia.has(recipe.proteinSource)) return false;

  // Ni mas de tres dias seguidos con la misma fuente.
  if (recipe.proteinSource !== 'none') {
    const ultimas = estado.fuentesPorDia.slice(-MENU_RULES.maxConsecutiveSameProtein);
    if (
      ultimas.length === MENU_RULES.maxConsecutiveSameProtein &&
      ultimas.every((fuente) => fuente === recipe.proteinSource)
    ) {
      return false;
    }
  }

  return true;
}

function mejorEscala(curva: MealCurve, objetivo: MacroTargets): { factor: number; error: number } {
  let mejor = { factor: curva.min, error: Number.POSITIVE_INFINITY };
  for (const factor of pasosDeEscala(curva)) {
    const error = errorScore(macrosAt(curva, factor), objetivo);
    if (error < mejor.error) mejor = { factor, error };
  }
  return mejor;
}

function puntuar(
  recipe: Recipe,
  objetivo: MacroTargets,
  fecha: LocalDate,
  estado: SeleccionEstado,
  ctx: MenuContext,
  ventana: number,
): number {
  const curva = mealCurve(recipe);
  const ajuste = mejorEscala(curva, objetivo);
  // El error normalizado ya viene en «tolerancias»: 0 es perfecto y 4 es un
  // desastre en los cuatro macros a la vez.
  const macroFit = Math.max(0, 1 - ajuste.error / 4);

  const ultimo = estado.ultimoUso.get(recipe.slug);
  const variedad =
    ultimo === undefined ? 1 : Math.min(1, daysBetweenLocal(ultimo, fecha) / (ventana * 2));

  const favorito = (ctx.favorites ?? []).includes(recipe.slug) ? 1 : 0;

  const despensa = new Set(ctx.pantryFoodSlugs ?? []);
  const enDespensa =
    despensa.size === 0
      ? 0
      : recipe.ingredients.filter((ingrediente) => despensa.has(ingrediente.foodSlug)).length /
        recipe.ingredients.length;

  return (
    MENU_WEIGHTS.macroFit * macroFit +
    MENU_WEIGHTS.variety * variedad +
    MENU_WEIGHTS.favorite * favorito +
    MENU_WEIGHTS.pantry * enDespensa +
    MENU_WEIGHTS.quality * qualityScore(recipe)
  );
}

// --------------------------------------------------------------------------
// Escalado conjunto del dia
// --------------------------------------------------------------------------

function optimizarEscalas(
  curvas: readonly MealCurve[],
  factores: number[],
  objetivoDia: MacroTargets,
): number[] {
  const actuales = [...factores];
  for (let ronda = 0; ronda < MENU_RULES.scaleRounds; ronda += 1) {
    for (let i = 0; i < curvas.length; i += 1) {
      const curva = curvas[i]!;
      let mejorFactor = actuales[i]!;
      let mejorError = Number.POSITIVE_INFINITY;
      for (const factor of pasosDeEscala(curva)) {
        actuales[i] = factor;
        const total = sumMacros(curvas.map((c, j) => macrosAt(c, actuales[j]!)));
        const error = errorScore(total, objetivoDia);
        if (error < mejorError) {
          mejorError = error;
          mejorFactor = factor;
        }
      }
      actuales[i] = mejorFactor;
    }
  }
  return actuales;
}

// --------------------------------------------------------------------------
// Items de ajuste
// --------------------------------------------------------------------------

/**
 * Anade items de ajuste hasta que el conjunto cierre o no haya mejora posible.
 * Lo usa tambien el reajuste del dia (§7), que se enfrenta al mismo problema.
 */
export function addAdjustmentItems(
  totales: MacroTargets,
  objetivo: MacroTargets,
): { items: MealItem[]; totales: MacroTargets } {
  const items: MealItem[] = [];
  let actual = totales;

  for (let intento = 0; intento < MENU_RULES.maxAdjustmentItems; intento += 1) {
    if (closesWithinTolerance(actual, objetivo)) break;

    let mejor: { item: MealItem; totales: MacroTargets; error: number } | null = null;
    const errorActual = errorScore(actual, objetivo);

    for (const ajuste of ADJUSTMENT_ITEMS) {
      const food = requireFood(ajuste.foodSlug);
      for (let cantidad = ajuste.step; cantidad <= ajuste.maxAmount; cantidad += ajuste.step) {
        const factor = cantidad / 100;
        const candidato: MacroTargets = {
          kcal: actual.kcal + food.kcal100 * factor,
          proteinG: actual.proteinG + food.protein100 * factor,
          carbsG: actual.carbsG + food.carbs100 * factor,
          fatG: actual.fatG + food.fat100 * factor,
        };
        const error = errorScore(candidato, objetivo);
        if (error < errorActual && (mejor === null || error < mejor.error)) {
          mejor = {
            item: { foodSlug: ajuste.foodSlug, amount: cantidad, unit: food.unitBase },
            totales: candidato,
            error,
          };
        }
      }
    }

    if (mejor === null) break;
    items.push(mejor.item);
    actual = mejor.totales;
  }

  return { items, totales: actual };
}

// --------------------------------------------------------------------------
// Generacion
// --------------------------------------------------------------------------

interface DiaEnConstruccion {
  request: DayRequest;
  slots: MealSlot[];
  objetivosPorSlot: Map<MealSlot, MacroTargets>;
  elegidas: Map<MealSlot, Recipe>;
}

function fuentePrincipal(dia: DiaEnConstruccion): string {
  let mejor = 'none';
  let masProteina = -1;
  for (const [, recipe] of dia.elegidas) {
    const macros = macrosAt(mealCurve(recipe), 1);
    if (recipe.proteinSource !== 'none' && macros.proteinG > masProteina) {
      masProteina = macros.proteinG;
      mejor = recipe.proteinSource;
    }
  }
  return mejor;
}

/**
 * Afina la receta ingrediente a ingrediente, dentro del margen de cada uno.
 *
 * Escalar el plato entero por un solo factor mueve todos los macros a la vez,
 * asi que no sirve para corregir una PROPORCION. Un dia `strength_high` pide
 * hidratos altos con la grasa en su suelo del 20 %, y con escalado uniforme las
 * cuatro comidas acababan clavadas en su tope de 1,4 con los hidratos un 9 %
 * por debajo: sin margen para subirlos sin subir tambien la grasa.
 *
 * Descenso por coordenadas, primera mejora, sin aleatoriedad (ADR-007). Se
 * ejecuta solo al CERRAR el dia, no dentro de la busqueda de recetas: ahi se
 * evalua con escalado uniforme, que es barato, porque lo que se compara son
 * recetas y no gramajes.
 */
function afinarIngredientes(
  recipes: readonly Recipe[],
  factoresBase: readonly number[],
  objetivo: MacroTargets,
): number[][] {
  const planes = recipes.map((recipe, indice) =>
    recipe.ingredients.map((ingrediente) =>
      ingrediente.isScalable
        ? Math.min(
            Math.max(factoresBase[indice] ?? 1, ingrediente.scaleMinPct),
            ingrediente.scaleMaxPct,
          )
        : 1,
    ),
  );

  const totales = (): MacroTargets =>
    sumMacros(
      planes.map((factores, indice) =>
        itemsMacros(recipeToItemsScaled(recipes[indice]!, factores)),
      ),
    );

  let mejor = errorScore(totales(), objetivo);

  for (let ronda = 0; ronda < MENU_RULES.ingredientRounds; ronda += 1) {
    let mejoro = false;

    for (const [indice, recipe] of recipes.entries()) {
      const plan = planes[indice]!;
      for (const [posicion, ingrediente] of recipe.ingredients.entries()) {
        if (!ingrediente.isScalable) continue;

        for (const delta of [MENU_RULES.ingredientStep, -MENU_RULES.ingredientStep]) {
          const previo = plan[posicion]!;
          const propuesto = roundTo(
            Math.min(
              Math.max(previo + delta, ingrediente.scaleMinPct),
              ingrediente.scaleMaxPct,
            ),
            3,
          );
          if (propuesto === previo) continue;

          plan[posicion] = propuesto;
          const error = errorScore(totales(), objetivo);
          if (error < mejor - 1e-9) {
            mejor = error;
            mejoro = true;
            break;
          }
          plan[posicion] = previo;
        }
      }
    }

    if (!mejoro) break;
  }

  return planes;
}

function construirDia(
  dia: DiaEnConstruccion,
  afinar = false,
  /** Macros que ya van a estar en el dia por otra via (items de ajuste). */
  yaCubierto: MacroTargets = ZERO_MACROS,
): {
  meals: MealDraft[];
  totals: MacroTargets;
} {
  const slots = dia.slots.filter((slot) => dia.elegidas.has(slot));
  const recipes = slots.map((slot) => dia.elegidas.get(slot)!);
  const curvas = recipes.map((recipe) => mealCurve(recipe));
  const iniciales = curvas.map((curva, indice) =>
    mejorEscala(curva, dia.objetivosPorSlot.get(slots[indice]!) ?? curva.fixed).factor,
  );
  const factores = optimizarEscalas(curvas, iniciales, dia.request.target);
  const objetivoRecetas: MacroTargets = {
    kcal: dia.request.target.kcal - yaCubierto.kcal,
    proteinG: dia.request.target.proteinG - yaCubierto.proteinG,
    carbsG: dia.request.target.carbsG - yaCubierto.carbsG,
    fatG: dia.request.target.fatG - yaCubierto.fatG,
  };
  const porIngrediente = afinar ? afinarIngredientes(recipes, factores, objetivoRecetas) : null;

  const meals: MealDraft[] = slots.map((slot, indice) => {
    const recipe = recipes[indice]!;
    const items =
      porIngrediente === null
        ? recipeToItems(recipe, factores[indice]!)
        : recipeToItemsScaled(recipe, porIngrediente[indice]!);
    return {
      slot,
      recipeSlug: recipe.slug,
      items,
      macros: roundMacros(itemsMacros(items)),
      scale: roundTo(factores[indice]!, 2),
      adjustmentItems: [],
    };
  });

  return { meals, totals: sumMacros(meals.map((meal) => meal.macros)) };
}

function errorDeDia(dia: DiaEnConstruccion): number {
  const { totals } = construirDia(dia);
  return errorScore(totals, dia.request.target);
}

/**
 * Genera el menu de una semana.
 *
 * Los dias se construyen en orden porque las reglas de variedad del §5.4 miran
 * hacia atras: cuantos dias seguidos llevan la misma fuente proteica, cuando se
 * uso por ultima vez cada receta. Un generador que resolviera los siete dias a
 * la vez tendria que modelar esas dependencias como restricciones globales, y
 * el resultado seria mas dificil de explicar sin ser mejor.
 */
export function generateWeeklyMenu(
  requests: readonly DayRequest[],
  ctx: MenuContext,
): WeeklyMenu {
  const ventana = ctx.varietyWindowDays ?? MENU_RULES.defaultVarietyWindowDays;
  const historial = new Map(
    (ctx.history ?? []).map((entrada) => [entrada.recipeSlug, entrada.usedOn] as const),
  );
  const estado: SeleccionEstado = {
    usosSemana: new Map(),
    ultimoUso: new Map(historial),
    historial,
    fuentesPorDia: [],
  };

  const construidos: DiaEnConstruccion[] = [];
  const reasonCodes: string[] = [];

  for (const request of requests) {
    const slots = normalizeMealSlots(request.slots);
    const reparto = splitDayIntoSlots({
      day: request.target,
      slots,
      bodyWeightKg: ctx.bodyWeightKg,
      dayType: request.dayType,
      timing: request.trainingTimeOfDay ?? null,
    });

    const dia: DiaEnConstruccion = {
      request,
      slots,
      objetivosPorSlot: new Map(reparto.map((parte) => [parte.slot, parte.target])),
      elegidas: new Map(),
    };

    const fuentesDelDia = new Set<string>();

    // Paso 2 y 3 del §5.2: filtrar y elegir por puntuacion, hueco a hueco.
    for (const slot of slots) {
      const objetivo = dia.objetivosPorSlot.get(slot)!;
      const candidatas = RECIPES.filter((recipe) =>
        esApta(recipe, slot, request.date, estado, ctx, fuentesDelDia, ventana),
      );
      if (candidatas.length === 0) {
        reasonCodes.push(`no_candidates_${request.date}_${slot}`);
        continue;
      }
      const elegida = candidatas.reduce((mejor, actual) => {
        const puntoMejor = puntuar(mejor, objetivo, request.date, estado, ctx, ventana);
        const puntoActual = puntuar(actual, objetivo, request.date, estado, ctx, ventana);
        if (puntoActual > puntoMejor) return actual;
        // Empate resuelto por slug: el orden del catalogo no debe decidir.
        if (puntoActual === puntoMejor && actual.slug < mejor.slug) return actual;
        return mejor;
      });

      dia.elegidas.set(slot, elegida);
      if (elegida.proteinSource !== 'none') fuentesDelDia.add(elegida.proteinSource);
      estado.usosSemana.set(elegida.slug, (estado.usosSemana.get(elegida.slug) ?? 0) + 1);
      estado.ultimoUso.set(elegida.slug, request.date);
    }

    // Paso 5: busqueda local. Se cambia un hueco por otro candidato mientras
    // el error total del dia baje.
    busquedaLocal(dia, request, estado, ctx, ventana);

    estado.fuentesPorDia.push(fuentePrincipal(dia));
    construidos.push(dia);
  }

  repararRotacionProteica(construidos, estado, ctx, ventana, reasonCodes);

  const days = construidos.map((dia) => cerrarDia(dia));

  return {
    days,
    reasonCodes,
    fishDays: days.filter((dia) => diaTieneFuente(dia, ['fish', 'shellfish'])).length,
    legumeDays: days.filter((dia) => diaTieneFuente(dia, ['legume'])).length,
  };
}

function busquedaLocal(
  dia: DiaEnConstruccion,
  request: DayRequest,
  estado: SeleccionEstado,
  ctx: MenuContext,
  ventana: number,
): void {
  let evaluaciones = 0;
  let mejorError = errorDeDia(dia);
  let mejoro = true;

  // Ascenso de primera mejora, repetido hasta que ninguna sustitucion mejore.
  // Una sola pasada por los huecos se queda corta, y ademas es arbitraria: lo
  // que le conviene al desayuno depende de lo que acabe habiendo en la cena.
  // Con cinco huecos y ocho candidatas por hueco, una pasada gasta cuarenta de
  // las doscientas evaluaciones del presupuesto y deja el resto sin usar.
  while (mejoro && evaluaciones < MENU_RULES.localSearchIterations) {
    mejoro = false;

    for (const slot of dia.slots) {
      const actual = dia.elegidas.get(slot);
      if (!actual) continue;
      const objetivo = dia.objetivosPorSlot.get(slot)!;

      const fuentesSinEste = new Set(
        [...dia.elegidas]
          .filter(([otro]) => otro !== slot)
          .map(([, recipe]) => recipe.proteinSource)
          .filter((fuente) => fuente !== 'none'),
      );

      const candidatas = RECIPES.filter(
        (recipe) =>
          recipe.slug !== actual.slug &&
          esApta(recipe, slot, request.date, estado, ctx, fuentesSinEste, ventana),
      )
        // Solo se prueban las que prometen algo por si solas: recorrer las
        // sesenta recetas en cada hueco no mejora y multiplica el trabajo.
        .sort(
          (a, b) =>
            puntuar(b, objetivo, request.date, estado, ctx, ventana) -
            puntuar(a, objetivo, request.date, estado, ctx, ventana),
        )
        .slice(0, 8);

      for (const candidata of candidatas) {
        if (evaluaciones >= MENU_RULES.localSearchIterations) return;
        evaluaciones += 1;

        dia.elegidas.set(slot, candidata);
        const error = errorDeDia(dia);
        if (error < mejorError) {
          mejorError = error;
          liberarUso(estado, actual.slug);
          estado.usosSemana.set(candidata.slug, (estado.usosSemana.get(candidata.slug) ?? 0) + 1);
          estado.ultimoUso.set(candidata.slug, request.date);
          mejoro = true;
          // Primera mejora: se acepta el cambio y se pasa al SIGUIENTE hueco.
          break;
        }
        dia.elegidas.set(slot, actual);
      }
    }
  }
}

function diaTieneFuente(dia: DayMenu, fuentes: readonly string[]): boolean {
  return dia.meals.some((meal) => fuentes.includes(requireRecipe(meal.recipeSlug).proteinSource));
}

function diaEnConstruccionTieneFuente(
  dia: DiaEnConstruccion,
  fuentes: readonly string[],
): boolean {
  return [...dia.elegidas.values()].some((recipe) => fuentes.includes(recipe.proteinSource));
}

/**
 * Rotacion obligatoria del §5.4: pescado y legumbre al menos dos dias cada uno.
 *
 * Se repara al final y no durante la seleccion a proposito: forzar pescado el
 * lunes porque «hacen falta dos» produce peores dias que dejar que el generador
 * elija y corregir despues los que menos pierden con el cambio.
 */
function repararRotacionProteica(
  dias: DiaEnConstruccion[],
  estado: SeleccionEstado,
  ctx: MenuContext,
  ventana: number,
  reasonCodes: string[],
): void {
  const objetivos: readonly { fuentes: readonly string[]; minimo: number; codigo: string }[] = [
    { fuentes: ['fish', 'shellfish'], minimo: MENU_RULES.minFishDays, codigo: 'fish' },
    { fuentes: ['legume'], minimo: MENU_RULES.minLegumeDays, codigo: 'legume' },
  ];

  for (const objetivo of objetivos) {
    let cumplen = dias.filter((dia) => diaEnConstruccionTieneFuente(dia, objetivo.fuentes)).length;
    if (cumplen >= objetivo.minimo) continue;

    const candidatosDia = dias.filter((dia) => !diaEnConstruccionTieneFuente(dia, objetivo.fuentes));

    // Se prueba cada dia y cada hueco principal, y se aplica el cambio mas
    // barato en terminos de error del dia.
    while (cumplen < objetivo.minimo) {
      let mejor: { dia: DiaEnConstruccion; slot: MealSlot; recipe: Recipe; coste: number } | null =
        null;

      for (const dia of candidatosDia) {
        if (diaEnConstruccionTieneFuente(dia, objetivo.fuentes)) continue;
        const errorPrevio = errorDeDia(dia);

        for (const slot of ['lunch', 'dinner'] as const) {
          const actual = dia.elegidas.get(slot);
          if (!actual) continue;

          const alternativas = RECIPES.filter(
            (recipe) =>
              objetivo.fuentes.includes(recipe.proteinSource) &&
              esApta(recipe, slot, dia.request.date, estado, ctx, new Set(), ventana),
          );

          for (const alternativa of alternativas) {
            dia.elegidas.set(slot, alternativa);
            const coste = errorDeDia(dia) - errorPrevio;
            dia.elegidas.set(slot, actual);
            if (mejor === null || coste < mejor.coste) {
              mejor = { dia, slot, recipe: alternativa, coste };
            }
          }
        }
      }

      if (mejor === null) {
        reasonCodes.push(`rotation_unmet_${objetivo.codigo}`);
        break;
      }

      const anterior = mejor.dia.elegidas.get(mejor.slot)!;
      mejor.dia.elegidas.set(mejor.slot, mejor.recipe);
      estado.usosSemana.set(anterior.slug, (estado.usosSemana.get(anterior.slug) ?? 1) - 1);
      estado.usosSemana.set(mejor.recipe.slug, (estado.usosSemana.get(mejor.recipe.slug) ?? 0) + 1);
      estado.ultimoUso.set(mejor.recipe.slug, mejor.dia.request.date);
      reasonCodes.push(`rotation_fixed_${objetivo.codigo}_${mejor.dia.request.date}`);
      cumplen += 1;
    }
  }
}

function cerrarDia(dia: DiaEnConstruccion): DayMenu {
  const { meals, totals } = construirDia(dia, true);
  const reasonCodes: string[] = [`day_type_${dia.request.dayType}`];

  let comidas = meals;
  let totales = totals;

  if (!closesWithinTolerance(totales, dia.request.target) && meals.length > 0) {
    const ajuste = addAdjustmentItems(totales, dia.request.target);
    if (ajuste.items.length > 0) {
      const macrosAjuste = itemsMacros(ajuste.items);

      // Se REHACEN los platos sabiendo que el ajuste va a estar ahi. Anadirlo
      // encima de un dia ya cuadrado en kcal lo descuadra: sumar 40 g de arroz
      // para arreglar los hidratos se lleva por delante la tolerancia de kcal.
      // Descontandolo del objetivo, las recetas se encogen lo justo para
      // dejarle sitio, que es lo que dice el §5.3 al hablar de anadir el item
      // «antes de dar el dia por valido».
      const rehecho = construirDia(dia, true, macrosAjuste);
      comidas = rehecho.meals;

      // Los items de ajuste van al hueco que mas lejos esta de su objetivo:
      // anadirlos siempre al mismo convertiria una comida en un cajon.
      const destino = comidas.reduce((mejor, actual) =>
        actual.macros.kcal < mejor.macros.kcal ? actual : mejor,
      );
      // OJO: `items` pasa a contener TAMBIEN los de ajuste, y
      // `adjustmentItems` es un SUBCONJUNTO suyo, no un complemento. Es lo que
      // exige el contrato: `plannedMeal.items` es lo que se guarda y `macros`
      // tiene que ser lo que suman esos items. Concatenar los dos campos cuenta
      // la comida dos veces.
      destino.adjustmentItems = ajuste.items;
      destino.items = [...destino.items, ...ajuste.items];
      destino.macros = roundMacros(itemsMacros(destino.items));
      totales = sumMacros(comidas.map((meal) => meal.macros));
      reasonCodes.push('adjustment_items_added');
    }
  }

  const cierra = closesWithinTolerance(totales, dia.request.target);
  if (!cierra) {
    reasonCodes.push('day_partial');
    // QUE macro se queda fuera, no solo que el dia no cierra. «Partial» a secas
    // no se puede explicar en pantalla ni arreglar desde fuera; «los hidratos
    // se quedan cortos» dice si el problema es el objetivo, el catalogo o el
    // numero de comidas. Con pocas comidas al dia es el caso normal: menos
    // platos, menos margen de escalado, y un residuo que ya no cabe en ninguno.
    const desvio = normalizedDeviation(totales, dia.request.target);
    if (desvio.kcal > 1) reasonCodes.push('partial_kcal');
    if (desvio.protein > 1) reasonCodes.push('partial_protein');
    if (desvio.carbs > 1) reasonCodes.push('partial_carbs');
    if (desvio.fat > 1) reasonCodes.push('partial_fat');
    if (comidas.length <= 3) reasonCodes.push('partial_few_meals');
  }

  return {
    date: dia.request.date,
    dayType: dia.request.dayType,
    target: dia.request.target,
    meals: comidas,
    totals: roundMacros(totales),
    status: cierra ? 'ok' : 'partial',
    deviation: normalizedDeviation(totales, dia.request.target),
    reasonCodes,
  };
}

/** Totales de una lista de comidas ya planificadas. */
export function menuTotals(meals: readonly { macros: MacroTargets }[]): MacroTargets {
  return meals.length === 0 ? { ...ZERO_MACROS } : sumMacros(meals.map((meal) => meal.macros));
}
